import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Star,
  User,
  MapPin,
  Film,
  Tv,
  Briefcase,
  Facebook
} from 'lucide-react';
import WatchlistButton from './WatchlistButton';

const PersonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Selected filmography category (a TMDb department, e.g. 'Acting', 'Directing').
  // null falls back to the person's headline department (see activeCat below).
  const [activeCategory, setActiveCategory] = useState(null);

  // API base URL - defaults to local development
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    if (id) {
      fetchPersonDetails();
    }
  }, [id]);

  const fetchPersonDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Always fetch fresh from the API. A cached sessionStorage copy was
      // masking server-side credit changes (e.g. the chat-show/news/self
      // filter) — showing stale roles after the fix shipped.
      await fetchFromAPI();

    } catch (error) {
      console.error('Error fetching person details:', error);
      setError('Failed to load person details. Please try again.');
      setLoading(false);
    }
  };

  const fetchFromAPI = async () => {
    try {
      const response = await fetch(`${API_BASE}/person/${id}`, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPerson(data);

    } catch (error) {
      console.error('API Error:', error);
      setError('Person not found or failed to load details.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (birthday, deathday) => {
    if (!birthday) return null;
    const birthDate = new Date(birthday);
    const endDate = deathday ? new Date(deathday) : new Date();
    const age = endDate.getFullYear() - birthDate.getFullYear();
    return age;
  };

  // A single filmography poster card. Fixed shape regardless of text length:
  // the poster is a fixed height, the title always reserves two lines, and the
  // meta (role/year/rating) is pinned to the bottom — so with the grid's
  // auto-rows-fr every card ends up the same dimensions.
  const renderCard = (credit, roleText, key) => (
    <Link
      key={key}
      to={credit.media_type === 'tv' ? `/tv/${credit.id}` : `/movie/${credit.id}`}
      onClick={() => sessionStorage.setItem(`movie_${credit.id}`, JSON.stringify(credit))}
      className="group block h-full"
    >
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow h-full flex flex-col">
        {credit.poster_path ? (
          <img src={credit.poster_path} alt={credit.title} className="w-full h-64 object-cover flex-shrink-0" />
        ) : (
          <div className="w-full h-64 bg-gradient-to-br from-videbits-100 to-videbits-200 flex items-center justify-center flex-shrink-0">
            {credit.media_type === 'tv' ? (
              <Tv className="w-16 h-16 text-videbits-400" />
            ) : (
              <Film className="w-16 h-16 text-videbits-400" />
            )}
          </div>
        )}
        <div className="p-3 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-1 mb-1">
            <h3 className="font-medium text-gray-900 text-sm line-clamp-2 min-h-[2.5rem] group-hover:text-videbits-600 transition-colors flex-1">
              {credit.title}
            </h3>
            {credit.media_type === 'tv' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-videbits-100 text-videbits-700 rounded font-medium flex-shrink-0">TV</span>
            )}
          </div>
          <div className="mt-auto">
            {roleText && (
              <p className="text-xs text-gray-600 line-clamp-1">{roleText}</p>
            )}
            {credit.release_date && (
              <p className="text-xs text-gray-500 mt-1">{credit.release_date.split('-')[0]}</p>
            )}
            {credit.vote_average > 0 && (
              <div className="flex items-center mt-2">
                <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                <span className="text-xs text-gray-600 ml-1">{credit.vote_average.toFixed(1)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-videbits-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading person details...</p>
        </div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <p className="text-red-700">{error || 'Person not found'}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  const age = calculateAge(person.birthday, person.deathday);
  const castCredits = person.cast_credits || [];
  const crewCredits = person.crew_credits || [];

  // Build an IMDb-style categorised filmography from the flat cast/crew credits.
  // Acting = cast; crew is grouped by TMDb department (Directing, Writing,
  // Production, …). A person can legitimately appear in several categories for
  // one title (e.g. wrote + directed), matching IMDb.
  const categories = {};
  if (castCredits.length) {
    categories['Acting'] = castCredits.map((c) => ({
      ...c,
      _role: c.character ? `as ${c.character}` : 'Actor',
    }));
  }
  crewCredits.forEach((c) => {
    const dept = c.department || 'Crew';
    (categories[dept] = categories[dept] || []).push({
      ...c,
      _role: c.job || dept,
    });
  });

  // Order the groups by how much work the person has in each — highest first —
  // so their primary craft (most credits, from the credits' own department tags)
  // leads. Ties broken alphabetically for stable ordering.
  const orderedCategories = Object.keys(categories).sort(
    (a, b) => categories[b].length - categories[a].length || a.localeCompare(b)
  );

  // Default to the biggest group (highest first).
  const defaultCategory = orderedCategories[0];
  const activeCat = activeCategory && categories[activeCategory] ? activeCategory : defaultCategory;
  // Reverse chronological (newest first); undated titles sort to the end.
  const byYearDesc = (arr) =>
    [...arr].sort((a, b) => (b.release_date || '').localeCompare(a.release_date || ''));
  const activeCredits = activeCat ? byYearDesc(categories[activeCat]) : [];

  const hasCredits = castCredits.length > 0 || crewCredits.length > 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-videbits-600 hover:text-videbits-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/logo-transparent.svg"
              alt="VideBits Logo"
              className="w-8 h-8"
            />
            <h1 className="text-xl font-light text-gray-900">VideBits</h1>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Person Header */}
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          {/* Profile Picture */}
          <div className="flex-shrink-0">
            {person.profile_path ? (
              <img
                src={person.profile_path}
                alt={person.name}
                className="w-64 h-64 md:w-80 md:h-80 rounded-lg object-cover shadow-lg mx-auto md:mx-0"
              />
            ) : (
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-lg bg-gradient-to-br from-videbits-100 to-videbits-200 flex items-center justify-center shadow-lg mx-auto md:mx-0">
                <User className="w-32 h-32 text-videbits-400" />
              </div>
            )}
          </div>

          {/* Person Info */}
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-4">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                {person.name}
              </h1>
              <WatchlistButton entityType="person" tmdbId={Number(id)} name={person.name} verb="follow" />
            </div>

            {/* Basic Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-2 text-gray-700">
                <Briefcase className="w-5 h-5 text-videbits-600" />
                <span className="font-medium">Known For:</span>
                <span>{person.known_for_department}</span>
              </div>

              {person.birthday && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Calendar className="w-5 h-5 text-videbits-600" />
                  <span className="font-medium">Born:</span>
                  <span>{formatDate(person.birthday)}</span>
                  {age && <span className="text-gray-500">({age} years old)</span>}
                </div>
              )}

              {person.deathday && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Calendar className="w-5 h-5 text-videbits-600" />
                  <span className="font-medium">Died:</span>
                  <span>{formatDate(person.deathday)}</span>
                </div>
              )}

              {person.place_of_birth && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <MapPin className="w-5 h-5 text-videbits-600" />
                  <span className="font-medium">Birthplace:</span>
                  <span>{person.place_of_birth}</span>
                </div>
              )}

              {person.popularity && (
                <div className="flex items-center space-x-2 text-gray-700">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">Popularity:</span>
                  <span>{person.popularity.toFixed(1)}</span>
                </div>
              )}
            </div>

            {/* Also Known As */}
            {person.also_known_as && person.also_known_as.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Also Known As:</h3>
                <div className="flex flex-wrap gap-2">
                  {person.also_known_as.slice(0, 5).map((alias, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                    >
                      {alias}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* External Links */}
            <div className="flex flex-wrap gap-3">
              {person.imdb_id && (
                <a
                  href={`https://www.imdb.com/name/${person.imdb_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
                >
                  IMDb Profile
                </a>
              )}
              {person.homepage && (
                <a
                  href={person.homepage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors text-sm font-medium"
                >
                  Official Website
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Biography */}
        {person.biography && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Biography</h2>
            <div className="prose max-w-none text-gray-700 leading-relaxed">
              {person.biography.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Filmography — categorised by department (IMDb-style) */}
        {hasCredits && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Filmography</h2>

            {/* Category selector */}
            <div className="flex flex-wrap gap-2 mb-6">
              {orderedCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeCat === cat
                      ? 'bg-videbits-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat} ({categories[cat].length})
                </button>
              ))}
            </div>

            {/* Selected category (newest first) */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 auto-rows-fr gap-6">
              {activeCredits.map((credit, index) =>
                renderCard(credit, credit._role, `${activeCat}-${credit.id}-${index}`)
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <img
                src="/logo-transparent.svg"
                alt="VideBits Logo"
                className="w-8 h-8"
              />
              <span className="text-gray-600">© 2024 VideBits. All rights reserved.</span>
            </div>
            <div className="flex items-center space-x-6 text-sm">
              <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">
                About
              </Link>
              <Link to="/privacy" className="text-gray-600 hover:text-gray-900 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-600 hover:text-gray-900 transition-colors">
                Terms
              </Link>
              <Link to="/help" className="text-gray-600 hover:text-gray-900 transition-colors">
                Help
              </Link>
              <a
                href="https://www.facebook.com/videbits"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="VideBits on Facebook"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PersonDetail;
